import axios from "axios";
import {Link, redirect} from "react-router-dom";
import {formatStartOrEndTime, formatRelativeEnd} from "../utils.js";
import {SanitizedSolrHighlightHtml} from "../components/sanitizedsolrhighlighthtml.js";
import {SearchResultDropdownPill} from "./searchresultdropdownpill.js";
import {useState, useEffect, useRef} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import {library as fontAwesomeLibrary} from "@fortawesome/fontawesome-svg-core";
import {SearchResultsPagination} from "./searchresultspagination";
import {SearchResultsCountSnippet} from "./searchresultscountsnippet.js";


const columns = [
    {
        // title properties here don't do anything but remind me
        // what the entry is
        name: "Dataset Title",
        key: "title",
        alt: "title",
        sortable: true,
    },
    {
        name: "Time Resolution",
        key: "cadence",
        alt: "cadence_human",
        sortable: true,
    },
    {
        name: "Start Time",
        key: "startTime",
        alt: "nominal_start_time",
        sortable: true,
    },
    {
        name: "End Time",
        key: "endTime",
        alt: "nominal_relative_end nominal_end_time",
        sortable: true,
    },
    {
        name: "Data Access",
        key: "hasHttps",
        sortable: false,
    },
    {
        name: "API",
        key: "hasApi",
        sortable: false,
    },
    {
        name: "FTP",
        key: "hasFtp",
        sortable: false,
    }
];

/**
 * Table of dataset search results, with links to individual dataset pages.
 *
 * @param props.searchData (backend response holding search results list)
 * @param props.rawFacetRestricts (facet restrictions as string)
 * @param props.url (For fetching new data from server)
 * @param props.pageNum (current page number)
 * @param props.missionId (optional mission identifier, if loaded from the mission page)
 * @param props.regionId (optional region node identifier, if loaded from the region page)
 * @param props.requestTimeToComplete (number of seconds backend response took)
 *
 * @return JSX for displaying the component
 */
export function SearchResultsTable(props) {
    const {
        searchData,
        query,
        rawFacetRestricts,
        url,
        pageNum,
        dateRange,
        missionId,
        regionId,
        requestTimeToComplete,
        bottomVariant,
        defaultSort = "",
        defaultOrder = ""
    } = props;
    const sortState = ['neutral', 'asc', 'desc'];
    const initialColumn = defaultSort ? columns.findIndex(c => c.alt === defaultSort) : -1;
    const initialStateIdx = defaultOrder ? Math.max(sortState.indexOf(defaultOrder), 0) : 0;

    const [sortColumn, setSortColumn] = useState(initialColumn);
    const [curState, setCurState] = useState(initialStateIdx);
    const [sortDirection, setSortDirection] = useState(sortState[initialStateIdx]);
    const [sortData, setSortData] = useState(defaultSort ? `&sort=${defaultSort}` : null);
    const [order, setOrder] = useState(defaultOrder ? `&order=${defaultOrder}` : null);
    const [page, setPage] = useState(pageNum);
    const [resultsDateRange, setResultsDateRange] = useState(null);
    const [itemStart, setItemStart] = useState(searchData.startIdx);
    const [numReturned, setNumReturned] = useState(searchData.numReturned);
    const [curRes, setCurRes] = useState(searchData.results);
    const tableRef = useRef(null);

    fontAwesomeLibrary.add(faSort);
    fontAwesomeLibrary.add(faSortUp);
    fontAwesomeLibrary.add(faSortDown);

    useEffect(() => {
        console.log(`Useeffect setting page to ${pageNum}`)
        setItemStart(searchData.startIdx);
        setNumReturned(searchData.numReturned);
        setCurRes(searchData.results);
        setPage(pageNum);
        setResultsDateRange(dateRange);
    }, [searchData, pageNum, dateRange]);

    useEffect(() => {
        const col = defaultSort ? columns.findIndex(c => c.alt === defaultSort) : -1;
        const idx = defaultOrder ? Math.max(sortState.indexOf(defaultOrder), 0) : 0;
        setSortColumn(col);
        setCurState(idx);
        setSortDirection(sortState[idx]);
        setSortData(defaultSort ? `&sort=${defaultSort}` : null);
        setOrder(defaultOrder ? `&order=${defaultOrder}` : null);
     }, [query, rawFacetRestricts, defaultSort, defaultOrder]);


    const handlePageChange = async (p) => {
        const {pageNum} = p;
        const response = await axios.get(getUrl(url, pageNum, query, rawFacetRestricts, sortData, order, missionId, regionId, resultsDateRange));
        setItemStart(response.data.searchData?.startIdx || response.data.startIdx);
        setNumReturned(response.data.searchData?.numReturned || response.data.numReturned);
        setCurRes((missionId || regionId) ? response.data.searchData?.results || response.data.results : response.data.results);
        tableRef.current.scrollIntoView({ behavior: 'auto' });
        setPage(pageNum);
    };

    const handleSort = async (columnIndex) => {
        let newState = curState + 1 < sortState.length ? curState+1 : 0;
        const newColumnSelected = sortColumn !== columnIndex;

        if (newColumnSelected) {
            newState = 1;
            setSortColumn(columnIndex);
            setPage(0);
            setItemStart(searchData.startIdx);
            setNumReturned(searchData.numReturned);
        }

        setSortDirection(sortState[newState]);
        setCurState(newState);

        const newPageNum = pageNum;
        const isNeutral = sortState[newState] === sortState[0];
        const ord = !isNeutral ? `&order=${sortState[newState]}` : '';
        const sortField = !isNeutral ? `&sort=${columns[columnIndex].alt}` : '';

        try {
            let response;


            if(!isNeutral) {
                response = await axios.get(getUrl(url, newPageNum, query, rawFacetRestricts, sortField, ord, missionId, regionId, resultsDateRange));
                setSortData(sortField);
                setOrder(ord);
            } else {
                response = await axios.get(getUrl(url, newPageNum, query, rawFacetRestricts, defaultSort, defaultOrder, missionId, regionId, resultsDateRange));
                setSortData(null);
                setOrder(null);
            }
            setItemStart(response.data.startIdx);
            setNumReturned(response.data.numReturned);
            setCurRes((missionId || regionId) ? response.data.searchData.results : response.data.results);
        } catch (error) {
            console.log(`Error is . .. ${error}`);
            return redirect("/error");
        }

        return null;
    };

    const loadPage = (columns, dataFromServer) => {
        const data = dataFromServer.map(rowData => {
            var rowDisplay = {
                id: rowData.id,
                title: [],
                cadence: null,
                startTime: null,
                endTime: null,
                hasHttps: null,
                hasApi: null,
                hasFtp: null,
            };

            if (rowData.highlighting.title) {
                rowDisplay.title.push(
                    <Link to={`/dataset/${rowData.id}`}>
                      <SanitizedSolrHighlightHtml
                        className="searchResultsTitle"
                        html={rowData.highlighting.title[0]}
                      />
                    </Link>
                );
            } else {
                rowDisplay.title.push(
                    <Link to={`/dataset/${rowData.id}`}>
                      <div className="searchResultsTitle">
                        {rowData.title}
                      </div>
                    </Link>
                );
            }

            if (rowData.highlighting.description) {
                rowDisplay.title.push(
                    <SanitizedSolrHighlightHtml
                      className="searchResultsDescription searchResultsDescriptionClamped"
                      maxChars={440}
                      html={rowData.highlighting.description[0]} />
                );
            } else {
                rowDisplay.title.push(
                    <div className="searchResultsDescription">
                      {rowData.shortDescription}
                    </div>
                );
            }

            if (rowData.highlighting.variables) {
                /* Clean up the display of variables. Sometimes variables have commas,
                 * which is confusing because we're already using commas. So we replace
                 * commas with a dash. Sometimes there are blank entries, so we remove
                 * those.
                 */
                const variablesHtml = (
                    rowData
                        .highlighting
                        .variables
                        .filter(entry => entry)
                        .map(entry => entry.replace(/,/g, ' -'))
                );

                rowDisplay.title.push(
                    <div className="searchResultsDescription">
                      <strong>Variables:</strong>&nbsp;

                      {variablesHtml.map(
                          (entry, idx) =>
                              <span>
                                <SanitizedSolrHighlightHtml
                                  style={{display: "inline"}}
                                  html={entry} />
                                {(idx < variablesHtml.length - 1) ? ", " : ""}
                              </span>
                      )}
                    </div>
                );
            }

            if (rowData.highlighting.authors) {
                rowDisplay.title.push(
                    <div className="searchResultsDescription">
                      <strong>Authors:</strong>&nbsp;
                      <SanitizedSolrHighlightHtml
                        style={{display: "inline"}}
                        html={rowData.highlighting.authors[0]} />
                    </div>
                );
            }

            if (rowData.cadence) {
                rowDisplay.cadence = rowData.cadenceHuman;
            }

            if (rowData.nominalStartTime) {
                rowDisplay.startTime = formatStartOrEndTime(rowData.nominalStartTime);
            }

            if (rowData.nominalRelativeEnd) {
                rowDisplay.endTime = formatRelativeEnd(rowData.nominalRelativeEnd);
            }
            else if (rowData.nominalEndTime) {
                rowDisplay.endTime = formatStartOrEndTime(rowData.nominalEndTime);
            }

            if (rowData.hasHttps) {
                rowDisplay.hasHttps = (
                    <SearchResultDropdownPill
                      pillLabel= "HTTPS"
                      type="http"
                      hcutId = {rowData.id}
                    />
                );
            }

            if (rowData.hasApi) {
                rowDisplay.hasApi = (
                    <SearchResultDropdownPill
                      pillLabel= "API"
                      type="api"
                      hasS3= {rowData?.hasHeliocloudS3}
                      hcutId = {rowData.id}
                    />
                );
            }

            if (rowData.hasFtp) {
                rowDisplay.hasFtp = (
                    <SearchResultDropdownPill
                      pillLabel= "FTP"
                      type="ftp"
                      hcutId = {rowData.id}
                    />
                );
            }
            return rowDisplay;
        });

        return data;
    };

    // Exit early condition: do not display anything if there is only one page
    if (!searchData.results || searchData.results.length === 0) {
        return (
            <SearchResultsCountSnippet
              searchData={searchData}
              requestTimeToComplete={requestTimeToComplete}
              itemStart={-1}
              query={query}
              dateRange={resultsDateRange}
              numReturned={numReturned} />
        );
    }

    let tableClassNames;

    if (bottomVariant) {
        tableClassNames = "searchResultsTableBottomVariant searchResultsTable";
    } else {
        tableClassNames = "searchResultsTable";
    }

    return (
        <>
        <SearchResultsCountSnippet
          searchData={searchData}
          requestTimeToComplete={requestTimeToComplete}
          itemStart={itemStart}
          query={query}
          dateRange={resultsDateRange}
          numReturned={numReturned} />
          <table key={query} className={tableClassNames} ref={tableRef}>
          <thead>
            <tr>
              <SortableHeaderCell
                label="Dataset Title"
                classNm="searchResultsTableHeaderDatasetTitle"
                columnSpan={1}
                columnIndex={0}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                sortable={columns[0].sortable}
                bottomVariant={bottomVariant}
              />
              <SortableHeaderCell
                label="Time Resolution"
                classNm="searchResultsTableHeaderTimeRes"
                columnSpan={1}
                columnIndex={1}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                sortable={columns[1].sortable}
                bottomVariant={bottomVariant}
              />
              <SortableHeaderCell
                label="Start Time"
                classNm="searchResultsTableHeaderStartTime"
                columnSpan={1}
                columnIndex={2}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                sortable={columns[2].sortable}
                bottomVariant={bottomVariant}
              />
              <SortableHeaderCell
                label="End Time"
                classNm="searchResultsTableHeaderEndTime"
                columnSpan={1}
                columnIndex={3}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                sortable={columns[3].sortable}
                bottomVariant={bottomVariant}
              />
              <SortableHeaderCell
                label="Access Methods"
                classNm="searchResultsTableHeaderDataAccess"
                columnSpan={3}
                columnIndex={4}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                handleSort={handleSort}
                sortable={columns[4].sortable}
                bottomVariant={bottomVariant}
              />
            </tr>
          </thead>

          <tbody>
            {loadPage(columns, curRes).map(
                rowData => {
                    return (
                        <tr>
                          {columns.map((col,i) =>
                                       <td
                                         key={i}
                                         className={`searchResultsTableTdRow searchResultsTableTd${col.key}`}>
                                         {rowData[col.key]}
                                       </td>
                          )}
                        </tr>
                    );
                })}
          </tbody>
        </table>
        <div>
          <SearchResultsPagination
            searchData={searchData}
            onNewPageSelect = {handlePageChange}
            pageNum= {page} />
        </div>
      </>
    );
}


/**
 * React component to display a table header as either sortable or not.
 *
 * @param label string that is used to label column.
 * @param classNm string that is used as 'className' for styling.
 * @param columnSpan integer that is used for 'column span' for a particular column.
 * @param columnIndex integer that identifies the position of the column.
 * @param sortColumn integer that identifies the column which is used to sort the received data.
 * @param sortDirection determines the direction of sorting. ('asc', 'desc')
 * @param handleSort function that handles the sorting.
 * @param sortable a boolean that determines if the column can be used for sorting.
 * @return JSX component
 */
const SortableHeaderCell = ({label, classNm, columnSpan, columnIndex, sortColumn, sortDirection, handleSort, sortable, bottomVariant}) => {
    const isActive = sortColumn === columnIndex;
    let arrow = "fa-solid fa-sort";

    if(isActive) {
        if(sortDirection === 'asc') {
            arrow = "fa-solid fa-sort-up";
        }
        else if(sortDirection === 'desc') {
            arrow = "fa-solid fa-sort-down";
        }
    }

    let classNames = `searchResultsTableHeader ${classNm}`;
    if (bottomVariant) {
        classNames += " searchResultsTableHeaderBottomVariant";
    }

    return (
        <th className={classNames} colSpan={columnSpan}>
            {label}
            {sortable &&
            <button onClick={() => handleSort(columnIndex)}><FontAwesomeIcon icon={`${arrow}`} /></button>}
        </th>
    );
};


/**
 * Create a url from the given parameters. Url could include querying, sorting, and pagination.
 *
 * @param url string that is the base home url.
 * @param pageNum integer for paginatino.
 * @param rawFacetRestricts string that is facet restrict.
 * @param sortQuery string a keyword used for sorting.
 * @param order string that determines sort order. ('asc', 'desc')
 * @param missionId is id of a mission.
 * @return url
 */
function getUrl(url, pageNum = null, query = null, rawFacetRestricts = null,
                sortQuery = null, order = null, missionId = null, regionId = null, dateRange = null) {

    let keyArray = [query, rawFacetRestricts, sortQuery];
    let numOfKeys = keyArray.reduce((numOfNonNull,x) => (x ? numOfNonNull+1 : numOfNonNull), 0);
    let mUrl = url ? url : `/heliodata/app/load_search_page`;

    /* Handle mission and region pages */
    mUrl = missionId ? mUrl + `/${encodeURIComponent(missionId)}` : mUrl;
    mUrl = regionId ? mUrl + `/${encodeURIComponent(regionId)}` : mUrl;

    /* Handle page, query, facetsrestricts, sort and sort order*/
    mUrl = pageNum ? mUrl + `/${encodeURIComponent(pageNum)}` : mUrl;
    mUrl = numOfKeys > 0 ? mUrl + `?` : mUrl;
    mUrl = query && query!=="" ? mUrl + `q=${encodeURIComponent(query)}|***|` : mUrl;
    numOfKeys = query && query!=="" ? --numOfKeys : numOfKeys;
    mUrl = numOfKeys > 0 ? mUrl + `&` : mUrl;
    mUrl = rawFacetRestricts ? mUrl + `${rawFacetRestricts}` : mUrl;
    numOfKeys = rawFacetRestricts ? --numOfKeys : numOfKeys;

    /* Handle sorting*/
    mUrl = numOfKeys > 0 ? mUrl + `&` : mUrl;
    mUrl = sortQuery ? mUrl.replace('|***|',`${sortQuery}${order}|***|`) : mUrl;
    mUrl = (missionId || regionId) && sortQuery ? mUrl + `${sortQuery}${order}` : mUrl;

   /* Handle date range*/
    mUrl = numOfKeys > 0 ? mUrl + `&` : mUrl;
    let histoStr = "";

    if (Array.isArray(dateRange) && dateRange.length > 0) {
        histoStr = `&nominal_start_time_ranges=${encodeURIComponent(dateRange.map((r) => `${r.from}/${r.to}`).join(","))}`;
    }
    mUrl = mUrl.replace('|***|', histoStr);
    mUrl = (missionId || regionId) && sortQuery ? mUrl + `${sortQuery}${order}` : mUrl;
    return mUrl;
}
